import { isProduct, type MaterialRow, type PackagePart, type PriceListData, type TagRow } from "./meta";

/*
 * Product tags (Price List): a tag set (or a loose tag) can say what a product is (Keyway, Brand: TagSetRow forProducts), not how an
 * item's sold (Supply only). Those go on products (item_tags). An item linked to products (packages.ts) has theirs too, as well as its own: a Brava deadlock's keyway is the installed one's. Everything that reads tags
 * (names, lists, filters: smartTags.ts tagIdsOf) reads them all, through tagsByItem. Shared by the server and the screens; no
 * server or browser imports.
 */

/** What working them out needs: without products' (a name read, a preview), an item's tags are its own. */
export type TagData = Pick<PriceListData, "tags" | "tagSets" | "itemTags"> & Partial<Pick<PriceListData, "materials" | "packages">>;

/** Whether a tag says what a product is: its set's say, or (loose) its own. */
export function isProductTag(data: Pick<PriceListData, "tagSets">, tag: Pick<TagRow, "setId" | "forProducts">): boolean {
    if (!tag.setId) return tag.forProducts;
    return data.tagSets.find((s) => s.id === tag.setId)?.forProducts ?? false;
}

// Worked out once per data load (every row asks).
const cache = new WeakMap<object, Record<string, string[]>>();

/**
 * Every item's tags (item, product or bundle uuid -> tag ids): its own; an item linked to products, their product tags too (but of
 * a set it has its own of). Unordered.
 */
export function tagsByItem(data: TagData): Record<string, string[]> {
    const known = cache.get(data);
    if (known) return known;
    const own = data.itemTags;
    if (!data.materials || !data.packages) {
        cache.set(data, own);
        return own;
    }
    const products = new Map(data.materials.filter(isProduct).map((m) => [m.uuid, own[m.uuid] ?? []]));
    const productTag = new Set(data.tags.filter((t) => isProductTag(data, t)).map((t) => t.id));
    const setOf = new Map(data.tags.map((t) => [t.id, t.setId]));
    const out: Record<string, string[]> = { ...own };
    for (const [uuid, parts] of Object.entries(data.packages)) {
        const mine = own[uuid] ?? [];
        // Its own of a set wins over its products' (one tagged before its set went on products, say).
        const ownSets = new Set(mine.flatMap((id) => setOf.get(id) ?? []));
        const inherited = inheritedTags(parts, products, productTag).filter((id) => !ownSets.has(setOf.get(id) ?? ""));
        if (inherited.length) out[uuid] = [...new Set([...mine, ...inherited])];
    }
    cache.set(data, out);
    return out;
}

/** The product tags an item's products have, in its products' order. */
function inheritedTags(parts: readonly PackagePart[], products: ReadonlyMap<string, readonly string[]>, productTag: ReadonlySet<string>): string[] {
    return parts.flatMap((p) => (products.get(p.uuid) ?? []).filter((id) => productTag.has(id)));
}

/** Where an item's tags that aren't its own come from (its products): tag id -> the product's name. A product's are all its own. */
export function tagSources(data: TagData & Pick<PriceListData, "materials" | "packages">, uuid: string): Map<string, string> {
    const out = new Map<string, string>();
    const own = new Set(data.itemTags[uuid] ?? []);
    const all = tagsByItem(data);
    const m = data.materials.find((x) => x.uuid === uuid);
    if (!m || isProduct(m)) return out;
    for (const p of data.packages[uuid] ?? []) {
        const product = data.materials.find((x) => x.uuid === p.uuid);
        for (const id of all[p.uuid] ?? []) if (!own.has(id) && !out.has(id) && product && isProductTag(data, data.tags.find((t) => t.id === id) ?? { setId: null, forProducts: false })) out.set(id, product.name);
    }
    return out;
}

/** The sets (and loose tags) a product's tags come from: product ones; an item's, the rest (a plain item's, any). */
export function tagChoicesFor(data: Pick<PriceListData, "tags" | "tagSets" | "packages">, item: MaterialRow): (tag: TagRow) => boolean {
    if (isProduct(item)) return (t) => isProductTag(data, t);
    // Linked: what it is comes from its products.
    if (data.packages[item.uuid]) return (t) => !isProductTag(data, t);
    return () => true;
}
